<script lang="ts" setup>
import type { SnackBar, SnackBarConfig } from "~/types"

const showSnackbar = ref(false)
const config: Ref<SnackBarConfig> = ref({ message: "", color: "success", timeout: 5000 })
let timeoutId: ReturnType<typeof setTimeout> | null = null

const store = useSnackbarStore()
const { successMsg, errorMsg, icon, onClick, trigger } = storeToRefs(store)

const dismiss = () => {
	showSnackbar.value = false
	store.clear()
}

const show = (cfg: SnackBarConfig): void => {
	showSnackbar.value = true
	config.value.color = cfg.color
	config.value.message = cfg.message
	config.value.timeout = cfg.timeout
	config.value.customAction = cfg.customAction

	if (timeoutId) clearTimeout(timeoutId)
	if (cfg.timeout && cfg.timeout > 0) {
		timeoutId = setTimeout(() => {
			dismiss()
		}, cfg.timeout)
	}
}

defineExpose<SnackBar>({
	show
})

const useCustomAction = () => {
	if (config.value.customAction?.action != null) {
		config.value.customAction?.action()
		showSnackbar.value = false
	}
}

// Watching the trigger counter (bumped on every setSuccessMsg/setErrorMsg) ensures the
// watcher fires even when the message text is unchanged from a previous notification.
watch(trigger, () => {
	if (errorMsg.value) {
		show({
			color: "error",
			message: errorMsg.value,
			timeout: -1
		})
		return
	}
	if (successMsg.value) {
		const successConfig: SnackBarConfig = {
			color: "success",
			message: successMsg.value,
			timeout: 3000
		}
		if (icon.value) {
			successConfig.customAction = {
				icon: icon.value,
				action: onClick.value
			}
		}
		show(successConfig)
	}
})

const colorClass = computed(() => {
	switch (config.value.color) {
		case "error": return "bg-error text-white"
		case "success": return "bg-success text-white"
		case "warning": return "bg-warning text-white"
		default: return "bg-primary text-white"
	}
})
</script>

<template>
	<Teleport to="body">
		<Transition name="snackbar">
			<div v-if="showSnackbar" data-cy="snackbar"
				class="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-x-3 px-4 py-3 rounded shadow-lg min-w-64 max-w-xl"
				:class="colorClass">
				<span class="flex-1 text-sm">{{ config.message }}</span>
				<UButton v-if="config.customAction?.icon" :icon="config.customAction.icon"
					color="neutral" variant="ghost" size="sm" @click="useCustomAction()" />
				<UButton icon="i-mdi-close" color="neutral" variant="ghost" size="sm"
					@click="dismiss()" />
			</div>
		</Transition>
	</Teleport>
</template>

<style scoped>
.snackbar-enter-active,
.snackbar-leave-active {
	transition: opacity 0.3s, transform 0.3s;
}
.snackbar-enter-from,
.snackbar-leave-to {
	opacity: 0;
	transform: translate(-50%, 1rem);
}
</style>
